import type { LucideIcon } from 'lucide-react'
import { useState } from 'react'

export const MOTIVOS = [
  'Compensação de Dias Trabalhados Além da Jornada',
  'Falta de Internet',
  'Problema na Entrada/Saída',
  'Capacitação na Área',
  'Nojo',
  'Problema de Reconhecimento Facial',
  'Atestado',
  'Declaração de Comparecimento',
  'Outro',
] as const

export const statusBadge: Record<string, { label: string, cls: string }> = {
  AGUARDANDO_ANALISE: { label: 'Aguardando análise', cls: 'bg-[#FEF9E7] text-[#B7791F]' },
  APROVADO: { label: 'Aprovado', cls: 'bg-[#EAF7EE] text-[#1E8A45]' },
  APROVADO_COM_ABONO: { label: 'Aprovado com abono', cls: 'bg-[#EAF7EE] text-[#1E8A45]' },
  APROVADO_SEM_ABONO: { label: 'Aprovado sem abono', cls: 'bg-[#FEF9E7] text-[#B7791F]' },
  REPROVADO: { label: 'Reprovado', cls: 'bg-[#FEF2F2] text-[#B91C1C]' },
}

// Cor do selo de verificação por status: verde (aprovado/aprovado com abono),
// amarelo (aprovado sem abono) e vermelho (reprovado). Sem selo enquanto aguarda análise.
const seloCorPorStatus: Record<string, string> = {
  APROVADO: '#1E8A45',
  APROVADO_COM_ABONO: '#1E8A45',
  APROVADO_SEM_ABONO: '#D69E2E',
  REPROVADO: '#DC2626',
}

// Texto explicativo exibido na tooltip de cada selo, detalhando o significado
// da cor e a implicação de abono/compensação para o requerente.
const seloDescricaoPorStatus: Record<string, string> = {
  APROVADO: 'Aprovado: o requerimento foi validado pela análise.',
  APROVADO_COM_ABONO: 'Aprovado com abono: a ausência foi validada e abonada, sem necessidade de compensação.',
  APROVADO_SEM_ABONO: 'Aprovado sem abono: o requerimento foi aceite, mas a ausência não gera abono e deverá ser compensada.',
  REPROVADO: 'Reprovado: o requerimento foi indeferido pela análise.',
}

interface SeloVerificadoProps {
  status: string
  size?: number
}

/**
 * Selo estilo "verificado" com contorno floral (como o selo azul do Instagram),
 * mas com cor dinâmica de acordo com o resultado da análise do protocolo:
 * verde para aprovado, amarelo para aprovado sem abono e vermelho para reprovado.
 */
export function SeloVerificado({ status, size = 16 }: SeloVerificadoProps) {
  const [aberto, setAberto] = useState(false)
  const cor = seloCorPorStatus[status]
  if (!cor)
    return null

  const descricao = seloDescricaoPorStatus[status] || statusBadge[status]?.label || status

  return (
    <span
      className="relative inline-flex shrink-0"
      onMouseEnter={() => setAberto(true)}
      onMouseLeave={() => setAberto(false)}
      onFocus={() => setAberto(true)}
      onBlur={() => setAberto(false)}
      tabIndex={0}
    >
      <svg
        viewBox="0 0 24 24"
        width={size}
        height={size}
        className="shrink-0"
        role="img"
        aria-label={descricao}
      >
        <path
          fill={cor}
          d="M12 0.5 13.86 2.1 16.3 1.25 17.35 3.6 19.9 3.5 20.1 6.05 22.5 6.9 21.6 9.3 23.5 11 21.6 12.7 22.5 15.1 20.1 15.95 19.9 18.5 17.35 18.4 16.3 20.75 13.86 19.9 12 21.5 10.14 19.9 7.7 20.75 6.65 18.4 4.1 18.5 3.9 15.95 1.5 15.1 2.4 12.7 0.5 11 2.4 9.3 1.5 6.9 3.9 6.05 4.1 3.5 6.65 3.6 7.7 1.25 10.14 2.1Z"
        />
        <path
          d="M8 12.3 10.8 15 16 9.7"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>

      {aberto && (
        <span className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-[240px] px-2.5 py-1.5 rounded-lg bg-[#12141A] text-white text-[11.5px] leading-snug shadow-lg pointer-events-none">
          {descricao}
          <span className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-[#12141A]" />
        </span>
      )}
    </span>
  )
}

interface StatusBadgeProps {
  status: string
  icon?: LucideIcon
  comSelo?: boolean
}

function StatusBadge({ status, comSelo = false }: StatusBadgeProps) {
  const info = statusBadge[status] || { label: status, cls: 'bg-[#F1F2F4] text-[#4B5563]' }
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2.5 py-1 rounded-full shrink-0 whitespace-nowrap ${info.cls}`}>
      {comSelo && <SeloVerificado status={status} size={14} />}
      {info.label}
    </span>
  )
}

export default StatusBadge
